import { useField } from "formik";
import React, { type FocusEvent, type InputHTMLAttributes, type ReactNode, useCallback } from "react";

import styles from "./InputField.module.css";

export interface InputFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "name"> {
	label: string;
	leftAccessory?: ReactNode;
	name: string;
	rightAccessory?: ReactNode;
}

export const InputField = ({
	label,
	leftAccessory,
	name,
	onBlur,
	rightAccessory,
	type = "text",
	...props
}: InputFieldProps) => {
	const [field, meta] = useField<string>({ name });
	const hasError = Boolean(meta.touched && meta.error);
	const inputId = props.id ?? name;

	// Formik's own blur handler marks the field touched, so run it first and let the caller's handler
	// (which may rewrite the value, as the name field does) follow.
	const onFieldBlur = useCallback(
		(event: FocusEvent<HTMLInputElement>): void => {
			field.onBlur(event);
			onBlur?.(event);
		},
		[field, onBlur]
	);

	const inputClassNames = [
		styles.input,
		leftAccessory ? styles.inputWithLeftAccessory : "",
		rightAccessory ? styles.inputWithAccessory : "",
		hasError ? styles.errorInput : ""
	]
		.filter(Boolean)
		.join(" ");

	return (
		<label className={styles.field} htmlFor={inputId}>
			<span className={styles.label}>{label}</span>
			<span className={styles.controlWrap}>
				{leftAccessory && <span className={styles.leftAccessory}>{leftAccessory}</span>}
				<input
					aria-describedby={hasError ? `${name}-error` : undefined}
					aria-invalid={hasError}
					autoComplete="off"
					className={inputClassNames}
					id={inputId}
					type={type}
					{...props}
					{...field}
					onBlur={onFieldBlur}
				/>
				{rightAccessory && <span className={styles.rightAccessory}>{rightAccessory}</span>}
			</span>
			<span className={styles.message} id={`${name}-error`}>
				{hasError ? meta.error : ""}
			</span>
		</label>
	);
};
